import { EventType, useI18N } from "@sundae/react-hooks";
import { Button, SplitButton, TableV2, Text, Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import { useWalletDetails } from "@sundae/wallet-react";
import cx from "classnames";
import uniqBy from "lodash/uniqBy";
import { FC, useCallback, useMemo, useState } from "react";

import { MinusIcon } from "@heroicons/react/24/outline";
import { PencilIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { testIds } from "../../../constants/testIds.constants";
import { useYieldFarmingPositionTable as useYieldFarmingPositionTableV2 } from "../../../hooks/farms/useYieldFarmingPositionTable";
import { useYieldFarmingPositions } from "../../../hooks/farms/useYieldFarmingPositions";
import { useEventTracking } from "../../../hooks/useEventTracking";
import useIsLPAsset from "../../../hooks/useIsLPAsset";
import PendingTransactions from "../../../i18n/components/farms/PendingTransactions/PendingTransactions.trans";
import { Actions, useAppContext } from "../../../stores";
import { extractLpAssetsFromPositions } from "../../../utils/farms.utils";
import UnlockYieldFarmingPositionsDialog from "../../Dialogs/UnlockYieldFarmingPositionsDialog/UnlockYieldFarmingPositionsDialog";
import UpdateYieldFarmingPositionDialog from "../../Dialogs/UpdateYieldFarmingPositionDialog";
import { YieldFarmingPositionDialogContextProvider } from "./context";

interface IPositionTableProps {
  description?: string;
  hideButton?: boolean;
  title: string;
}

const PositionTable: FC<IPositionTableProps> = ({ hideButton, title, description }) => {
  const { details } = useWalletDetails();
  const { dispatch } = useAppContext();
  const { isLPAssetV1 } = useIsLPAsset();
  const {
    isLoading: isLoadingYieldFarmingPositions,
    mergedPosition,
    positions,
    lpAssetsPoolMap,
    pendingPositionTransactions,
    pendingDelegationTransactions,
    pendingYieldFarmingTransactions,
    pendingUnlockingPositionTransactions,
    setPendingYieldFarmingTransactions,
  } = useYieldFarmingPositions({
    beneficiary: [details.stakingAddress, details.mainAddress].sort(),
  });

  const {
    columns: columnsV2,
    columnWidths,
    tableData,
    tableHeaders,
    handleSort,
    sortConfig,
  } = useYieldFarmingPositionTableV2({ mergedPosition });

  const { t } = useI18N("farms");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUnlockDialogOpen, setIsUnlockDialogOpen] = useState(false);
  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleOpenUnlockDialog = () => setIsUnlockDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);
  const handleCloseUnlockDialog = () => setIsUnlockDialogOpen(false);
  const isLoading = useMemo(
    () =>
      isLoadingYieldFarmingPositions ||
      Boolean(pendingPositionTransactions.length) ||
      Boolean(pendingUnlockingPositionTransactions.length) ||
      Boolean(pendingDelegationTransactions.length),
    [
      isLoadingYieldFarmingPositions,
      pendingPositionTransactions.length,
      pendingUnlockingPositionTransactions.length,
      pendingDelegationTransactions.length,
    ]
  );
  const handleOnMutationSuccess = useCallback(
    (txHash: string) => {
      if (txHash) {
        setPendingYieldFarmingTransactions(
          uniqBy(
            [
              ...pendingYieldFarmingTransactions,
              {
                address: details?.stakingAddress,
                expires: Date.now() + 1000 * 60 * 10, // 10 minutes
                type: "position",
                txHash,
              },
            ],
            "txHash"
          )
        );
      }
    },
    [pendingYieldFarmingTransactions, details?.stakingAddress]
  );

  const loadingOverlayNode = useMemo(() => {
    if (
      Boolean(pendingPositionTransactions.length) ||
      Boolean(pendingUnlockingPositionTransactions.length)
    ) {
      return (
        <Tooltip
          trigger={
            <Text tag="p" size="xs">
              {t("portfolio.positions.tx.processing")}
            </Text>
          }
          tooltipLabel={
            <PendingTransactions
              context="position"
              transactions={pendingYieldFarmingTransactions}
            />
          }
        />
      );
    }
    if (isLoading)
      return (
        <Text tag="p" size="xs">
          {t("portfolio.positions.isLoading")}
        </Text>
      );
    return null;
  }, [isLoading, pendingPositionTransactions.length, pendingUnlockingPositionTransactions.length]);

  const hasYfMigrations = useMemo(() => {
    if (!positions) {
      return false;
    }

    return extractLpAssetsFromPositions(isLPAssetV1, positions).length > 0;
  }, [positions, isLPAssetV1]);

  const handleEventTracking = useEventTracking();

  const handleMigrateLiquidityClick = useCallback(() => {
    handleEventTracking({
      eventType: EventType.Click,
      source: "LiquidityPoolTableActions",
      value: `migrate_liquidity:yield_farming`,
    });
    Actions.setDialogState({ dialog: "liquidity.migrate", isOpen: true }, dispatch);
  }, [dispatch]);

  return (
    <>
      <div className="flex flex-col gap-6">
        <TableV2
          aria-label="Yield Farming Position"
          blurryVariant="primarySubtle"
          columnWidths={columnWidths}
          description={description}
          emptyStateProps={{
            ctaNode: (
              <Button
                onClick={handleOpenDialog}
                size="sm"
                variant="neutral"
                data-testid={testIds.yieldFarming.positions.ctas.create}
              >
                {t("portfolio.positions.create")}
              </Button>
            ),
            label: t("noData", { context: "yieldFarmingPositions", ns: "warnings" }),
          }}
          loadingProps={{
            isLoading:
              isLoading ||
              Boolean(pendingPositionTransactions.length) ||
              Boolean(pendingUnlockingPositionTransactions.length),
            loadingOverlayNode,
            loadingRows: 3,
          }}
          onSort={handleSort}
          rounded
          sortConfig={sortConfig}
          tableColumns={columnsV2}
          tableData={tableData}
          tableHeaderData={tableHeaders}
          title={title}
        />

        {!isLoading && Boolean(mergedPosition?.value?.length) && !hideButton ? (
          <div className={cx("flex justify-end gap-2", toolkitCx.layout.spacing.horizontal.md)}>
            <SplitButton
              actionListItems={[
                {
                  label: t("portfolio.positions.unlock"),
                  icon: MinusIcon,
                  onClick: handleOpenUnlockDialog,
                  show: true,
                },
                ...(hasYfMigrations
                  ? [
                      {
                        label: t("portfolio.positions.action"),
                        icon: PencilIcon,
                        onClick: handleOpenDialog,
                        show: true,
                      },
                    ]
                  : []),
              ]}
              primaryActionButtonProps={
                hasYfMigrations
                  ? {
                      className: "flex items-center justify-center gap-4",
                      variant: "gradient",
                      onClick: handleMigrateLiquidityClick,
                      size: "sm",
                    }
                  : {
                      onClick: handleOpenDialog,
                      testId: testIds.yieldFarming.positions.ctas.update,
                      variant: "neutral",
                      size: "sm",
                    }
              }
              secondaryActionButtonProps={{
                testId: testIds.yieldFarming.positions.ctas.unlockAll,
                variant: "neutral",
                size: "sm",
              }}
            >
              {hasYfMigrations ? (
                <>
                  <SparklesIcon className="h-4 w-4 !text-warning-300" />
                  {t("liquidity", { context: "migrate", ns: "liquidity" })}
                </>
              ) : (
                t("portfolio.positions.action")
              )}
            </SplitButton>
          </div>
        ) : null}
      </div>

      <YieldFarmingPositionDialogContextProvider mergedPosition={mergedPosition}>
        {/** We need the exchange context provider in order to use the error handler */}

        {isDialogOpen && (
          <UpdateYieldFarmingPositionDialog
            closeDialog={handleCloseDialog}
            lpAssetsPoolMap={lpAssetsPoolMap ?? new Map()}
            onOpenChange={setIsDialogOpen}
            onMutationSuccess={handleOnMutationSuccess}
            open={isDialogOpen}
            isLoading={isLoadingYieldFarmingPositions}
          />
        )}

        {isUnlockDialogOpen && (
          <UnlockYieldFarmingPositionsDialog
            closeDialog={handleCloseUnlockDialog}
            onOpenChange={setIsUnlockDialogOpen}
            open={isUnlockDialogOpen}
            onMutationSuccess={handleOnMutationSuccess}
          />
        )}
      </YieldFarmingPositionDialogContextProvider>
    </>
  );
};

export default PositionTable;
